
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Calendar, 
  Activity, 
  Stethoscope, 
  MessageSquare, 
  Settings,
  ShoppingCart,
  LogOut
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate } from "react-router-dom";
import { DashboardStats } from "../DashboardStats";
import { DashboardAnalytics } from "../DashboardAnalytics";
import { AppointmentsTab } from "../tabs/AppointmentsTab";
import { ServicesTab } from "../tabs/ServicesTab";
import { MessageList } from "../../MessageList";
import { ProfessionalProfile } from "../ProfessionalProfile";
import { PurchaseRequestsTabNew } from "../tabs/PurchaseRequestsTabNew";

const EnhancedProfessionalDashboard = () => {
  const [activeSection, setActiveSection] = useState("dashboard");
  const { session } = useAuth();
  const navigate = useNavigate();

  const { data: stats, isLoading } = useQuery({
    queryKey: ["professional-dashboard-stats", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      
      console.log('Fetching dashboard stats for user:', session.user.id);

      const [appointmentsResult, servicesResult, productsResult, messagesResult, purchaseRequestsResult] = await Promise.all([
        supabase.from("professional_appointments").select("*").eq("professional_id", session.user.id),
        supabase.from("professional_services").select("*").eq("professional_id", session.user.id),
        supabase.from("professional_products").select("*").eq("professional_id", session.user.id),
        supabase.from("messages").select("*").eq("receiver_id", session.user.id),
        supabase.from("product_purchase_requests").select("*").eq("professional_id", session.user.id)
      ]);

      const stats = {
        appointments: appointmentsResult.data?.length || 0,
        services: servicesResult.data?.length || 0,
        products: productsResult.data?.length || 0,
        messages: messagesResult.data?.length || 0,
        purchaseRequests: purchaseRequestsResult.data?.length || 0
      };

      console.log('Dashboard data fetched:', stats);
      return stats;
    },
    enabled: !!session?.user?.id,
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold">Dashboard</h1>
              <p className="text-gray-600">Vue d'ensemble de votre activité professionnelle</p>
            </div>
            <DashboardStats />
            <DashboardAnalytics />
          </div>
        );
      case "appointments":
        return <AppointmentsTab />;
      case "services":
        return <ServicesTab />;
      case "purchase-requests":
        return <PurchaseRequestsTabNew />;
      case "messages":
        return <MessageList />;
      case "profile":
        return <ProfessionalProfile />;
      default:
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold">Dashboard</h1>
              <p className="text-gray-600">Vue d'ensemble de votre activité professionnelle</p>
            </div>
            <DashboardStats />
            <DashboardAnalytics />
          </div>
        );
    }
  };

  const menuItems = [
    { key: "dashboard", label: "Dashboard", icon: Activity },
    { key: "appointments", label: "Rendez-vous", icon: Calendar },
    { key: "services", label: "Services et Produits", icon: Stethoscope },
    { key: "purchase-requests", label: "Demandes d'achat", icon: ShoppingCart },
    { key: "messages", label: "Conversations", icon: MessageSquare },
    { key: "profile", label: "Profil Utilisateur", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-80 bg-blue-600 text-white min-h-screen">
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-8">Médicace</h2>
            
            <nav className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    onClick={() => setActiveSection(item.key)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeSection === item.key
                        ? "bg-blue-500 text-white"
                        : "text-blue-100 hover:bg-blue-500 hover:text-white"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                    {item.key === "purchase-requests" && stats?.purchaseRequests > 0 && (
                      <Badge variant="secondary" className="ml-auto">
                        {stats.purchaseRequests}
                      </Badge>
                    )}
                    {item.key === "messages" && stats?.messages > 0 && (
                      <Badge variant="secondary" className="ml-auto">
                        {stats.messages}
                      </Badge>
                    )}
                    {item.key === "appointments" && stats?.appointments > 0 && (
                      <Badge variant="secondary" className="ml-auto">
                        {stats.appointments}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>

            <div className="mt-8 pt-8 border-t border-blue-500">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full flex items-center space-x-3 px-4 py-3 text-blue-100 hover:bg-blue-500 hover:text-white"
              >
                <LogOut className="h-5 w-5" />
                <span>Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default EnhancedProfessionalDashboard;
